import React from "react";
import type { Messages } from "../../core/api";
import { getFullName } from "../../shared/utils/get-full-name";
import {
  declareAiCapability,
  isPermittedForAi,
  setAiContextEntity,
  useProvideAiCapability,
} from "../ai-omnibar/ai-capability-registry";
import { useEntityProfileSectionAiCapability } from "../entityProfileModal/hooks/useEntityProfileSectionAiCapability";
import { caregiverProfileBridgeActions } from "./caregiverProfileModal.actions";
import {
  caregiverProfileSections,
  CaregiverProfileSection,
} from "./caregiverProfileModal.sections";
import { CaregiverProfileCaregiver } from "./caregiverProfileModal.types";

/**
 * What the caregiver profile can do for the AI omnibar - the mirror of
 * patientProfileModal.ai.ts, and the second entity to use the scope mechanism.
 *
 * `scope: "caregiver-profile"` is opened by `caregiver.openModal` (declared in config.js). Neither
 * side names the other: this file says which space these live in, that one says which spaces it
 * opens.
 */

declareAiCapability({
  id: "caregiver.section.open",
  title: "Go to a section of the caregiver profile",
  effect: "navigate",
  description:
    "Scrolls the open caregiver profile to one of its sections or subsections - calendar, profile details, compliance, certifications, documents, HR and so on.",
  location: "the section navigation of the caregiver profile",
  scope: "caregiver-profile",
  params: [
    {
      name: "section",
      type: "enum",
      required: true,
      description: "Which section of the profile to show.",
      // Declared statically so the model has the vocabulary while planning - before any profile is
      // open, nothing has contributed anything yet. Filtered by the same permission keys the
      // navigation honours. The mounted profile then contributes the exact set visible for that
      // record, and the runner resolves against those.
      options: () =>
        caregiverProfileSections.flatMap((section) =>
          section.subsections
            .filter((subsection) => isPermittedForAi(subsection.permissionKey))
            .map((subsection) => ({
              value: `${section.label}/${subsection.label}`,
              label:
                section.label === subsection.label
                  ? section.label
                  : `${section.label} > ${subsection.label}`,
            }))
        ),
    },
  ],
  examples: ["show Mercury's compliance", "open the documents for caregiver 41"],
});

declareAiCapability({
  id: "caregiver.notes.open",
  title: "Open the notes of the caregiver that is open",
  effect: "navigate",
  description:
    "Opens the Notes side bar of the caregiver profile, showing what has been recorded about that caregiver and letting the user add a note.",
  location: "the side bar of the caregiver profile",
  scope: "caregiver-profile",
  examples: ["notes on Mercury"],
});

declareAiCapability({
  id: "caregiver.absence.open",
  title: "Record an absence for the caregiver that is open",
  effect: "prefill",
  description:
    "Opens the new absence form on the caregiver's calendar, optionally on a given day. The user sets the dates and reason and confirms; nothing is recorded until they do.",
  location: "a dialog over the caregiver profile calendar",
  scope: "caregiver-profile",
  // The key the calendar itself checks before it shows the control - see
  // CaregiverProfileCalendarSection.tsx. Without it the model was told about a form the person
  // has no button for.
  permissionKey: "create/delete_caregiver_absence",
  params: [
    {
      name: "date",
      type: "date",
      required: false,
      description: "First day of the absence, as yyyy-MM-dd.",
    },
  ],
  examples: ["Mercury is out on Thursday", "log an absence for caregiver 41"],
});

declareAiCapability({
  id: "caregiver.changeStatus.open",
  title: "Open the status change dialog for the caregiver that is open",
  effect: "prefill",
  description:
    "Opens the dialog for changing a caregiver's employment status. It only opens the dialog - the user chooses the new status and confirms it themselves.",
  location: "a dialog over the caregiver profile",
  scope: "caregiver-profile",
  // The key the About section checks before it offers the status control.
  permissionKey: "edit_caregiver_status",
  examples: ["change Mercury's status"],
});

declareAiCapability({
  id: "caregiver.payRate.open",
  title: "Add a pay rate for the caregiver that is open",
  effect: "prefill",
  description:
    "Opens the pay rate form for this caregiver, empty and ready to fill in. The user sets the rate and dates and confirms; nothing is saved until they do.",
  location: "a dialog over the caregiver profile",
  scope: "caregiver-profile",
  permissionKey: "edit_caregiver_page_pay_rates" as Messages["AgencyMemberPermission"],
  examples: ["add a pay rate for Mercury"],
});

declareAiCapability({
  id: "caregiver.hoursOverride.open",
  title: "Add an hours override for the caregiver that is open",
  effect: "prefill",
  description:
    "Opens the hours override form for this caregiver, where the user records an adjustment to their hours and confirms it.",
  location: "a dialog over the caregiver profile",
  scope: "caregiver-profile",
  examples: ["override Mercury's hours"],
});

type SectionNavigation = {
  handleSectionChange: (index: number) => void;
  handleSubsectionChange: (index: number) => void;
};

export function useCaregiverProfileAiCapabilities(
  caregiver: CaregiverProfileCaregiver | undefined,
  navigation: SectionNavigation,
  visibleSections: CaregiverProfileSection[]
): void {
  const isReady = caregiver !== undefined;
  const requireCaregiver = () => {
    if (caregiver === undefined) {
      throw new Error("The caregiver profile is still loading.");
    }

    return caregiver;
  };

  const asDate = (value: unknown) => (typeof value === "string" ? value : undefined);

  useEntityProfileSectionAiCapability({
    capabilityId: "caregiver.section.open",
    sections: visibleSections,
    navigation,
    enabled: isReady,
  });

  useProvideAiCapability(
    "caregiver.notes.open",
    () => {
      const forWhom = requireCaregiver();

      caregiverProfileBridgeActions.openNotes({ caregiver: forWhom });

      return `Opened notes for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "caregiver.absence.open",
    (args) => {
      const forWhom = requireCaregiver();

      caregiverProfileBridgeActions.openAbsence({
        caregiver: forWhom,
        calendarDate: asDate(args.date),
      });

      return `Opened a new absence for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "caregiver.changeStatus.open",
    () => {
      const forWhom = requireCaregiver();

      caregiverProfileBridgeActions.openChangeStatus({ caregiver: forWhom });

      return `Opened the status change dialog for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "caregiver.payRate.open",
    () => {
      const forWhom = requireCaregiver();

      // No `data`: that is what the profile itself passes for "add" rather than "edit a row".
      caregiverProfileBridgeActions.openCaregiverPayRate({ caregiver: forWhom });

      return `Opened a new pay rate for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  useProvideAiCapability(
    "caregiver.hoursOverride.open",
    () => {
      const forWhom = requireCaregiver();

      caregiverProfileBridgeActions.openCaregiverHoursOverride({ caregiver: forWhom });

      return `Opened a new hours override for ${getFullName(forWhom)}`;
    },
    { enabled: isReady }
  );

  React.useEffect(() => {
    if (caregiver === undefined) {
      return;
    }

    return setAiContextEntity(
      "caregiver-profile",
      `Caregiver profile open: ${getFullName(caregiver)} (id ${
        caregiver.displayId ?? caregiver.id
      })`
    );
  }, [caregiver]);
}
